import React, { ReactElement, ReactNode } from 'react';
import { css } from '@emotion/react';
import classNames from 'classnames';
import { IconCircleInfo, IconCircleCheck, IconProps, IconCircleWarning, IconTriangleWarning } from '../icons/generated';
import { Stack } from '../stack';
import { semantic_colors, shapes, Typography } from '../../foundation';
import { text_style_specs } from '../../foundation/typography';
import { BaseText } from '../text';
import { CloseButton } from '../button';
import { banner_color_css, banner_background_color_css, banner_border_color_css } from './styles';

export type BannerStatus = 'neutral' | 'info' | 'error' | 'warning' | 'success';
export type BannerSize = 'xsmall' | 'small' | 'medium';
export type BannerDirection = 'horizontal' | 'vertical';

export interface BannerProps {
  className?: string;
  /** @default 'info' */
  status?: BannerStatus;
  /** @default 'medium' */
  size?: BannerSize;
  /** @default 'horizontal' */
  direction?: BannerDirection;
  /**
   * title 좌측에 렌더링 될 아이콘 요소
   *  - title이 없을 경우 렌더되지 않습니다.
   *  - Icon 컴포넌트 color props가 있더라도 status 따라 currentColor가 지정됩니다.
   *  - Icon 컴포넌트 size props가 있더라도 Icon 사이즈는 banner size 'medium'일경우 16, 'small'일경우 12로 고정됩니다.
   */
  leftIcon?: ReactElement<IconProps>;
  /** @default true */
  hasIcon?: boolean;
  title?: string;
  message?: ReactNode;
  /**
   * 배너 우측에 렌더링 될 extra actions
   */
  actions?: ReactNode;
  /**
   * 배너 우측에 렌더링 될 CloseButton 사용 유무
   * @default false
   */
  closable?: boolean;
  onClose?: () => void;
}

const CLOSE_ICON_INSET = 4;

const sizeStyles: Record<
  BannerSize,
  {
    wrapper: { p: string; rowGap: number; columnGap: number; actionGap: number };
    title: { kind: Typography };
    message: { kind: Typography };
    icon: { size: number; containerHeight: number };
  }
> = {
  xsmall: {
    wrapper: {
      p: '6px 8px',
      rowGap: 0,
      columnGap: 4,
      actionGap: 6,
    },
    title: {
      kind: 'Body_12_SemiBold',
    },
    message: {
      kind: 'Body_12_Regular',
    },
    icon: {
      size: 12,
      containerHeight: text_style_specs.Body_12.lineHeight,
    },
  },
  small: {
    wrapper: {
      p: '10px 12px',
      rowGap: 0,
      columnGap: 6,
      actionGap: 8,
    },
    title: {
      kind: 'Body_12_SemiBold',
    },
    message: {
      kind: 'Body_12_Regular',
    },
    icon: {
      size: 12,
      containerHeight: text_style_specs.Body_12.lineHeight,
    },
  },
  medium: {
    wrapper: {
      p: '14px 16px',
      rowGap: 2,
      columnGap: 8,
      actionGap: 8,
    },
    title: {
      kind: 'Body_14_SemiBold',
    },
    message: {
      kind: 'Body_13_Regular',
    },
    icon: {
      size: 16,
      containerHeight: text_style_specs.Body_14.lineHeight,
    },
  },
};

export const Banner = ({
  className,
  status = 'info',
  size = 'medium',
  direction = 'horizontal',
  leftIcon,
  hasIcon = true,
  title,
  message,
  actions,
  closable = false,
  onClose,
}: BannerProps) => {
  const color = banner_color_css[status];
  const style = sizeStyles[size];

  const renderToIcon = () => {
    const icon = leftIcon ?? React.createElement(default_icons[status] as React.ComponentType<IconProps>);

    return (
      <span
        css={css`
          display: inline-flex;
          flex-shrink: 0;
          align-items: center;
          height: ${style.icon.containerHeight}px;
        `}
      >
        {React.cloneElement(icon, { size: style.icon.size, color })}
      </span>
    );
  };

  return (
    <Stack
      direction='column'
      className={classNames('pds-normal-banner', className)}
      css={css`
        width: 100%;
        ${shapes.border_radius.small};
        background-color: ${banner_background_color_css[status]};
        border: 1px solid ${banner_border_color_css[status]};
        color: ${color};
        word-wrap: break-word;
      `}
      p={style.wrapper.p}
    >
      <Stack gap={`0 ${style.wrapper.columnGap}px`} align='flex-start'>
        {hasIcon && renderToIcon()}
        <Stack direction='column' gap={`${style.wrapper.rowGap}px 0`}>
          {title && (
            <BaseText typography={style.title.kind as Typography} color={color}>
              {title}
            </BaseText>
          )}
          {message && (
            <BaseText typography={style.message.kind as Typography} color={semantic_colors.content.primary}>
              {message}
            </BaseText>
          )}
          {direction === 'vertical' && (
            <Stack width='auto' align='flex-start' mt={style.wrapper.actionGap - style.wrapper.rowGap}>
              {actions}
            </Stack>
          )}
        </Stack>
        <Stack ml='auto' gap={`0 ${style.wrapper.columnGap}px`}>
          {direction === 'horizontal' && actions}
          {closable && (
            <div
              css={css`
                display: inline-flex;
                flex-shrink: 0;
                align-items: center;
                height: ${style.icon.size + CLOSE_ICON_INSET * 2}px;
              `}
            >
              <CloseButton size={style.icon.size} color={semantic_colors.content.secondary} onClick={onClose} />
            </div>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

const default_icons: { [key in BannerStatus]: React.ComponentType } = {
  neutral: IconCircleInfo,
  info: IconCircleInfo,
  error: IconCircleWarning,
  success: IconCircleCheck,
  warning: IconTriangleWarning,
} as const;
